import {
  Accordion,
  AccordionSummary,
  Avatar,
  Typography,
  AccordionDetails,
  Stack,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { IBurgerMenuItem } from 'API/Home/burgerMenuItems';
import { getSupplierRedirectCode } from 'API/Treatment/treatment';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { AccordionArrowWhite2 } from 'Images';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';
import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface IBurgerMenuAccordion {
  accordionIndex: number;
  burgerItem: IBurgerMenuItem;
  isExpanded: boolean;
  setExpandedAccordionIndex: Function;
  closeMenu?: Function;
}

export const BurgerMenuAccordion = ({
  accordionIndex,
  burgerItem,
  isExpanded,
  setExpandedAccordionIndex,
  closeMenu,
}: IBurgerMenuAccordion) => {
  const navigate = useNavigate();

  const { pathname } = useLocation();

  const theme = useTheme();
  const isScreenXl = useMediaQuery(theme.breakpoints.up('xl'));

  const eshetTursRedirectCode = 2,
    flyingCarpetRedirectCode = 1;

  const itemNavigationHandler = useCallback(
    async (item: IBurgerMenuItem) => {
      if (item.internalLink) {
        navigate(`/${item.internalLink}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (!isScreenXl && closeMenu) closeMenu();
      } else if (item.externalLink) {
        let code = 0;
        if (item.externalLink === 'https://shikum.eshet.com')
          code = eshetTursRedirectCode;
        if (item.externalLink === 'https://flying-shikum.co.il')
          code = flyingCarpetRedirectCode;

        if (code !== 0) {
          const redirectCode = await getSupplierRedirectCode(code);
          window.open(
            `${item.externalLink}/?code=${encodeURIComponent(redirectCode)}`
          );
        } else window.open(item.externalLink);
      }
    },
    [navigate, isScreenXl, closeMenu]
  );

  const memoizedBurgerMenuSubItems = useMemo(() => {
    return burgerItem.subItems?.map(
      (subItem: IBurgerMenuItem, index: number) => {
        return (
          <Typography
            key={index}
            variant={'body6'}
            color={'#fff'}
            sx={{
              padding: '20px 0 20px 60px',
              backgroundColor:
                subItem.internalLink && pathname === `/${subItem.internalLink}`
                  ? '#071b4a'
                  : undefined,
              transition: '.5s',
              '&:hover': {
                cursor: 'pointer',
                bgcolor: '#071b4a',
                transition: '.5s',
              },
            }}
            component='a'
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              itemNavigationHandler(subItem);
            }}
            onKeyDown={onEnterOrEscapeKeyDown}
          >
            {subItem.description}
            {subItem.isNewItem && (
              <Box
                sx={{
                  display: 'inline-block',
                  padding: '0 8px',
                  ml: '10px',
                  fontWeight: 700,
                  fontSize: '16px',
                  bgcolor: 'purple2.main',
                  borderRadius: '0 12px 0 6px',
                }}
              >
                {Dictionary[DICTIONARY.General].sidebar.new}
              </Box>
            )}
          </Typography>
        );
      }
    );
  }, [burgerItem.subItems, itemNavigationHandler, pathname]);

  return (
    <Accordion
      TransitionProps={{ unmountOnExit: true }}
      disableGutters
      expanded={isExpanded}
      sx={{
        backgroundColor: 'transparent',
        boxShadow: 'none',
        transition: '.3s',
        '&:before': { display: 'none' },
        '&:hover': {
          cursor: 'pointer',
          bgcolor: 'primary.shade_500',
          transition: '.3s',
        },
      }}
      onClick={() =>
        setExpandedAccordionIndex(isExpanded ? -1 : accordionIndex)
      }
    >
      <AccordionSummary
        expandIcon={
          burgerItem.subItems ? (
            <Avatar
              alt={''}
              src={AccordionArrowWhite2}
              sx={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                bgcolor: isExpanded ? 'purple2.main' : undefined,
                transition: '.3s',
              }}
            />
          ) : null
        }
        aria-controls='panel1a-content'
        id='panel1a-header'
        sx={{
          backgroundColor: isExpanded ? 'primary.shade_500' : 'transparent',
          padding: '10px 20px 10px 20px',
        }}
        onClick={() => itemNavigationHandler(burgerItem)}
      >
        <Stack direction={'row'} gap={'16px'}>
          <Avatar
            alt=''
            src={burgerItem.iconUrl}
            sx={{ height: '24px', width: '24px' }}
          />
          <Typography variant={'body6'} color={'#fff'}>
            {burgerItem.description}
          </Typography>
        </Stack>
      </AccordionSummary>
      {burgerItem.subItems ? (
        <AccordionDetails sx={{ bgcolor: 'primary.shade_600', padding: 0 }}>
          <Stack>{memoizedBurgerMenuSubItems}</Stack>
        </AccordionDetails>
      ) : null}
    </Accordion>
  );
};
